import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { useSelector } from "react-redux";
import YhTable, { YhTableColumn, YhTableCellParams } from "../../ui/YhTable";
import YhInput from "../../ui/YhInput";
import YhButton from "../../ui/YhButton";
import {
  formatDate,
  isTruthy,
  openErrorNotification,
  openSuccessNotification,
} from "../../helpers/methods";
import notifiers from "../../global/constants/NotificationConstants";
import urls from "../../global/constants/UrlConstants";
import {
  selectActiveOrganizationId,
} from "../../redux/authSlice";
import {
  archiveAutomationWorkflow,
  listAutomationWorkflows,
  publishAutomationWorkflow,
  AutomationWorkflow,
  toIdString,
  normalizeWorkflowRow,
} from "../../services/automationService";
import {
  OrgNameCell,
  useOrgNameMap,
} from "../../Shared/Organization/OrganizationSelect";
import {
  getCrmLeadPreferences,
  updateCrmLeadPreferences,
} from "../../services/organizationService";

const SearchSvg = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const statusChip = (status?: string) => {
  const s = String(status || "draft").toLowerCase();
  const map: Record<string, { bg: string; color: string }> = {
    draft: { bg: "#F3F4F6", color: "#4B5563" },
    published: { bg: "#D1FAE5", color: "#047857" },
    archived: { bg: "#FEE2E2", color: "#B91C1C" },
  };
  const theme = map[s] || map.draft;
  return (
    <span
      className="inline-flex rounded-full px-2.5 py-0.5 text-xs font-semibold capitalize"
      style={{ backgroundColor: theme.bg, color: theme.color }}
    >
      {s}
    </span>
  );
};

const AutomationList: React.FC = () => {
  const history = useHistory();
  const orgNameMap = useOrgNameMap();
  const activeOrganizationId = useSelector(selectActiveOrganizationId);
  const [rows, setRows] = useState<AutomationWorkflow[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [totalRows, setTotalRows] = useState(0);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25,
  });
  const [assignmentMode, setAssignmentMode] = useState<"separate" | "shared">(
    "separate"
  );
  const [savingMode, setSavingMode] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(t);
  }, [searchInput]);

  useEffect(() => {
    const orgId = String(activeOrganizationId || "").trim();
    if (!orgId || orgId === "all") return;
    (async () => {
      try {
        const res = await getCrmLeadPreferences(orgId);
        const data = res?.data || res;
        setAssignmentMode(
          data?.leadAssignmentMode === "shared" ? "shared" : "separate"
        );
      } catch (_) {
        setAssignmentMode("separate");
      }
    })();
  }, [activeOrganizationId]);

  const saveAssignmentMode = async (mode: "separate" | "shared") => {
    const orgId = String(activeOrganizationId || "").trim();
    if (!orgId || orgId === "all") {
      openErrorNotification("Select a specific organization first.");
      return;
    }
    try {
      setSavingMode(true);
      setAssignmentMode(mode);
      await updateCrmLeadPreferences(orgId, { leadAssignmentMode: mode });
      openSuccessNotification(
        mode === "separate"
          ? "Leads will be managed separately per assignee"
          : "Leads will share one stage across assignees"
      );
    } catch (error: any) {
      openErrorNotification(error.message || "Failed to save");
    } finally {
      setSavingMode(false);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    paginationModel.page,
    paginationModel.pageSize,
    search,
    activeOrganizationId,
  ]);

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await listAutomationWorkflows({
        page: paginationModel.page,
        limit: paginationModel.pageSize,
        search,
      });
      const raw = Array.isArray(res?.data)
        ? res.data
        : Array.isArray(res?.data?.data)
          ? res.data.data
          : Array.isArray(res)
            ? res
            : [];
      setRows(raw.map(normalizeWorkflowRow));
      setTotalRows(
        Number(res?.total ?? res?.data?.total ?? raw.length) || 0
      );
    } catch (error: any) {
      openErrorNotification(
        isTruthy(error.message) ? error.message : notifiers.GENERIC_ERROR
      );
    } finally {
      setLoading(false);
    }
  };

  const onNew = () => {
    if (!activeOrganizationId || activeOrganizationId === "all") {
      openErrorNotification("Select a specific organization before creating a workflow.");
      return;
    }
    history.push(urls.automationLeadFlowNewPath);
  };

  const goEdit = (rowOrId: any) => {
    const id = toIdString(
      typeof rowOrId === "object"
        ? rowOrId?._id ?? rowOrId?.id ?? rowOrId
        : rowOrId
    );
    if (!id || !/^[a-fA-F0-9]{24}$/.test(id)) {
      openErrorNotification("Invalid workflow id");
      return;
    }
    history.push(urls.automationLeadFlowEditPath.replace(":id", id));
  };

  const onPublish = async (id?: any) => {
    const wid = toIdString(id);
    if (!wid) return;
    try {
      await publishAutomationWorkflow(wid);
      openSuccessNotification("Workflow published");
      loadData();
    } catch (error: any) {
      openErrorNotification(error.message || "Failed to publish");
    }
  };

  const onArchive = async (id?: any) => {
    const wid = toIdString(id);
    if (!wid) return;
    try {
      await archiveAutomationWorkflow(wid);
      openSuccessNotification("Workflow archived");
      loadData();
    } catch (error: any) {
      openErrorNotification(error.message || "Failed to archive");
    }
  };

  const columns: YhTableColumn[] = [
    {
      headerName: "Name",
      field: "name",
      minWidth: 220,
      flex: 1,
      renderCell: (params: YhTableCellParams) => (
        <button
          type="button"
          className="text-left text-sm font-semibold text-[#A02448] hover:underline"
          onClick={(e) => {
            e.stopPropagation();
            goEdit(params.row);
          }}
        >
          {params.row?.name || "Untitled"}
        </button>
      ),
    },
    {
      headerName: "Trigger",
      field: "triggerType",
      minWidth: 140,
      renderCell: (params: YhTableCellParams) => (
        <span className="text-sm capitalize text-[#374151]">
          {String(params.row?.triggerType || "—").replace(/_/g, " ")}
        </span>
      ),
    },
    {
      headerName: "Scope",
      field: "scope",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => {
        const sc = String(
          params.row?.scope || params.row?.settings?.scope || "organization"
        );
        return (
          <span className="text-sm capitalize text-[#374151]">
            {sc.replace(/_/g, " ")}
          </span>
        );
      },
    },
    {
      headerName: "Status",
      field: "status",
      minWidth: 120,
      renderCell: (params: YhTableCellParams) => statusChip(params.row?.status),
    },
    {
      headerName: "Organization",
      field: "organizationId",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => (
        <OrgNameCell
          organizationId={params.row?.organizationId}
          map={orgNameMap}
        />
      ),
    },
    {
      headerName: "Updated",
      field: "updatedAt",
      minWidth: 160,
      renderCell: (params: YhTableCellParams) => {
        const raw = params.row?.updatedAt;
        if (!raw) return <span className="text-sm text-[#6B7280]">—</span>;
        const formatted = formatDate(raw);
        return (
          <span className="text-sm text-[#6B7280]">
            {formatted.includes("NaN") ? "—" : formatted}
          </span>
        );
      },
    },
    {
      headerName: "Actions",
      field: "actions",
      minWidth: 220,
      renderCell: (params: YhTableCellParams) => (
        <div className="flex flex-wrap items-center gap-2">
          <button
            type="button"
            className="text-xs font-semibold text-[#2563EB] hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              goEdit(params.row);
            }}
          >
            Edit
          </button>
          {params.row?.status !== "published" && (
            <button
              type="button"
              className="text-xs font-semibold text-[#047857] hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                onPublish(params.row._id);
              }}
            >
              Publish
            </button>
          )}
          {params.row?.status !== "archived" && (
            <button
              type="button"
              className="text-xs font-semibold text-[#B91C1C] hover:underline"
              onClick={(e) => {
                e.stopPropagation();
                onArchive(params.row._id);
              }}
            >
              Archive
            </button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-[calc(100vh-68px)] max-h-[calc(100vh-68px)] flex-col overflow-hidden bg-white p-4 sm:p-5 lg:p-6">
      <div className="mb-4 flex shrink-0 flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold tracking-tight text-[#111827]">
            Automation Lead Flow
          </h1>
          <p className="mt-1 text-sm text-[#6B7280]">
            Build global, project, or form lead-assign workflows. If an org has
            no assign flow, the global workflow is used.
          </p>
        </div>
        <div className="flex w-full max-w-xl flex-wrap items-center gap-3 sm:w-auto">
          <div className="min-w-[220px] flex-1">
            <YhInput
              value={searchInput}
              onChange={(e) => {
                setSearchInput(e.target.value);
                setPaginationModel((p) => ({ ...p, page: 0 }));
              }}
              placeholder="Search workflows"
              type="search"
              startAdornment={<SearchSvg />}
            />
          </div>
          <YhButton onClick={onNew}>New workflow</YhButton>
        </div>
      </div>

      <div className="mb-4 shrink-0 rounded-2xl border border-[#EEF0F5] bg-[#FAFBFC] p-4">
        <p className="text-sm font-semibold text-[#111827]">
          Lead assignment on team assign
        </p>
        <p className="mt-1 text-xs text-[#6B7280]">
          Default is Separate — each assignee manages their own stage on the
          same lead. Also configurable under CRM Preferences.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            disabled={savingMode}
            onClick={() => saveAssignmentMode("separate")}
            className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
              assignmentMode === "separate"
                ? "border-[#A02448] bg-[#FDF5F7] text-[#A02448]"
                : "border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]"
            }`}
          >
            Separate (per user stage)
          </button>
          <button
            type="button"
            disabled={savingMode}
            onClick={() => saveAssignmentMode("shared")}
            className={`rounded-xl border px-3 py-2 text-xs font-semibold transition ${
              assignmentMode === "shared"
                ? "border-[#A02448] bg-[#FDF5F7] text-[#A02448]"
                : "border-[#E5E7EB] bg-white text-[#374151] hover:bg-[#F9FAFB]"
            }`}
          >
            Shared (one stage for all)
          </button>
        </div>
      </div>

      <div className="relative min-h-0 flex-1">
        <YhTable
          columns={columns}
          rows={rows}
          getRowId={(row) => toIdString(row._id) || String(row.name || Math.random())}
          rowHeight={64}
          loading={loading}
          emptyMessage="No workflows yet"
          className="h-full min-h-[280px]"
          page={paginationModel.page}
          pageSize={paginationModel.pageSize}
          totalRows={totalRows}
          onPageChange={(p) =>
            setPaginationModel((prev) => ({ ...prev, page: p }))
          }
          onPageSizeChange={(s) =>
            setPaginationModel({ pageSize: s, page: 0 })
          }
          paginationLabel="workflows"
          onRowClick={(row) => goEdit(row)}
        />
      </div>
    </div>
  );
};

export default AutomationList;
